import { ReactElement } from 'react';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { Intent } from '@Components/designConstants';

interface NoEditAccessDialogProps {
  show: boolean;
  onHide: () => void;
  onSaveAsNew: () => void;
  /** The chart's owner. `null` on legacy rows, which is why the body has a second form. */
  createdBy: string | null;
}

/**
 * Shown when a user presses Update on a chart they may not change.
 *
 * A dialog on the attempt, rather than a disabled button. The requirement is to make the rule clear
 * when a user TRIES to act, and a disabled control cannot do that here.
 *
 * THE BODY MUST NAME BOTH ROUTES, and that matters more than it looks. A colleague who cannot edit a
 * chart can take a copy, or ask for the edit level to be widened. The second route is cheap: a
 * creator with role 1 can grant edit rights to role 2 or role 3 themselves, in one control, with no
 * administrator involved — and they keep their own access. A dialog offering only a copy reliably
 * produces two diverging charts where two people wanted one shared chart. The permission model is
 * not the problem in that case; the words in this dialog are.
 */
export const NoEditAccessDialog = ({
  show,
  onHide,
  onSaveAsNew,
  createdBy
}: NoEditAccessDialogProps): ReactElement => (
  <AlertDialog
    show={show}
    onHide={onHide}
    intent={Intent.WARNING}
    // Required, and a plain string — no markup.
    title="You don't have edit access to this chart"
    body={
      createdBy ? (
        <>
          It&apos;s owned by <strong>{createdBy}</strong>, who can give you edit access. You can
          also save your changes as a new chart.
        </>
      ) : (
        // `createdBy` is null on rows written before it was recorded. Never print the word "null".
        <>You can save your changes as a new chart, or ask an admin for edit access.</>
      )
    }
    primaryAction={{ onClick: onSaveAsNew, label: 'Save as a new chart' }}
    secondaryAction={{ onClick: onHide }}
  />
);
